import { Injectable } from '@angular/core';
import { AudioPlayTask, AudioTaskState } from '../audio-play-task';
import { BehaviorSubject } from 'rxjs';
import { AudioStreamDestination } from './audio-stream-destination';

@Injectable({ providedIn: 'root' })
export class AudioStreamTaskFactory {
    private _audioContext: AudioContext | null = null;
    private _streamNode: AudioWorkletNode | null = null;
    private _preInitPromise: Promise<void> | null = null;

    public preInit() {
        if (this._audioContext === null) {
            this._audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
                sampleRate: 24000,
                latencyHint: 'interactive', // Optimizes for low-latency streaming
            });

            this._preInitPromise = this._audioContext.audioWorklet.addModule(
                'assets/audio-player-processor.js',
            );
        }
    }

    public createAudioStreamTask(): [AudioPlayTask, AudioStreamDestination] {
        const audioContext = this._audioContext;
        if (audioContext === null || this._preInitPromise === null) {
            throw new Error('Audio context was not created, make sure preInit was called');
        }

        const task = new AudioStreamTask(this._preInitPromise, audioContext, () => {
            if (this._streamNode === null) {
                this._streamNode = new AudioWorkletNode(audioContext, 'audio-stream-processor');
                this._streamNode.connect(audioContext.destination);
            }

            return this._streamNode;
        });

        const incomingDataGateway = (data: Uint8Array | 'complete') => {
            if (data === 'complete') {
                task.playRemainingAndFinalize();
                
                return;
            }

            if (this._audioContext === null) {
                return;
            }

            if (task.isComplete()) {
                return;
            }

            const payload = () => {
                if (!this._streamNode) {
                    return;
                }

                // 1. Get the raw ArrayBuffer from your byte array
                const buffer = data.buffer || new Uint8Array(data).buffer;

                // 2. Read the bytes directly as 16-bit Signed Integers (Int16)
                // This automatically combines every 2 bytes into 1 audio sample
                const int16Array = new Int16Array(buffer);

                // 3. Allocate a Float32 array (exactly half the length of the original byte array)
                const float32Array = new Float32Array(int16Array.length);

                // 4. Normalize the Int16 range [-32768, 32767] to Float32 range [-1.0, 1.0]
                for (let i = 0; i < int16Array.length; i++) {
                    float32Array[i] = int16Array[i] / 32768.0;
                }

                this._streamNode.port.postMessage(float32Array);
            };

            if (this._audioContext.state === 'suspended' && !task.isPausedByUser()) {
                this._audioContext.resume().then(payload);
            } else {
                payload();
            }
        };

        return [task, incomingDataGateway];
    }
}

class AudioStreamTask implements AudioPlayTask {
    private readonly stateSubject = new BehaviorSubject<AudioTaskState>('unknown');

    private _sessionPauseTimerRef: ReturnType<typeof setTimeout> | null = null;
    private _streamNode: AudioWorkletNode | null = null;
    private _pausedByUser = false;
    private _waitingForFinalization = false;

    private currentTaskProcessorListener = (message: MessageEvent) => {
        if (message.data === 'buffer-empty') {
            if (this._waitingForFinalization) {
                // We know data is not coming for this task any more so can finalize
                this.finalizeTask();
                this._sessionPauseTimerRef = setTimeout(() => {
                    this.audioContext.suspend();
                }, 100);
            }
        }
    };

    public state = this.stateSubject.asObservable();

    constructor(
        private readonly preInitPromise: Promise<void>,
        private readonly audioContext: AudioContext,
        private readonly streamNodeFactory: () => AudioWorkletNode,
    ) {}

    public async init() {
        this.unscheduleSessionPause();

        await this.preInitPromise;
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        this.stateSubject.next('playing');

        const streamNode = this.streamNodeFactory();
        this._streamNode = streamNode;
        streamNode.port.addEventListener('message', this.currentTaskProcessorListener);
        streamNode.port.start();
    }

    public async resume() {
        this._pausedByUser = false;
        await this.audioContext.resume();
        this.stateSubject.next('playing');
    }

    public async pause() {
        this._pausedByUser = true;
        await this.audioContext.suspend();
        this.stateSubject.next('paused');
    }

    public async stop() {
        this._streamNode?.port.postMessage('clear');
        await this.audioContext.suspend();
        this.finalizeTask();
    }

    public isComplete() {
        return this.stateSubject.value === 'complete';
    }

    public isPausedByUser() {
        return this._pausedByUser;
    }

    public playRemainingAndFinalize() {
        // We do not expect any more data coming at this point.
        // So we set the marker to wait whatever playing to complete and finalize task.
        this._waitingForFinalization = true;
    }

    private unscheduleSessionPause() {
        if (this._sessionPauseTimerRef !== null) {
            clearTimeout(this._sessionPauseTimerRef);
        }
    }

    private finalizeTask() {
        this.stateSubject.next('complete');
        this.stateSubject.complete();
        this._streamNode?.port.removeEventListener('message', this.currentTaskProcessorListener);
    }
}
